// Setting up a scenario, on a screen of its own.
//
// It was an expansion of the menu's third card — difficulty chips and a list
// unfolding under a heading, with the other two doors still sitting there
// underneath. That was fine while it was two chips and five boards, and it
// stopped being fine the moment a scenario had *settings*: sliders, a seed and
// a choice of who plays, wedged into a card that also had to explain what a
// scenario is.
//
// A screen, then. The doors pick what kind of game; this picks which one and
// how it runs, and it is the only thing on the page while you do.
//
// **The layout was `ScenarioScreen.css` and is the kit now** (PLAN-UI §28.0,
// priority 2): the squares, the two columns and the fold are `styled()` on
// `scenario/controls.tsx`, every hint is a `HoverCard` rather than a `title`,
// and every colour is a token. What is left in here is what the screen *is*:
// state, and which of it goes where.

import { Fragment, useState } from "react";

import type { Catalog, LevelName as Level, Tuning } from "src/api";
// Still the engine's, until the contract carries them (PLAN-CLIENT-SPLIT §4):
// `layoutFor` for which named layout a team table is, `scenarioById` for whether a board has
// teams, `SKILL`/`TUNING_MAX` for the sliders' defaults and reach, and
// `armyName` for a side's name before any board exists.
import { layoutFor, scenarioById } from "src/engine/ai/scenarios";
import { SKILL, TUNING_MAX } from "src/engine/ai/difficulty";
import { armyName } from "src/engine/session/armyName";
import { useCatalog } from "./menu/useCatalog";
import { FRAME_CAPS } from "src/client/ai/render/frameCap";
import {
  Btn,
  Collapse,
  Field,
  HoverCard,
  Page,
  PageColumn,
  PageTitle,
  Panel,
  Slider,
  Text,
  styled
} from "./kit";
import {
  ArmyHead,
  ArmySide,
  ArmyTable,
  ArmyYou,
  BoardTile,
  Boards,
  Choice,
  Picker,
  Setting
} from "./scenario/controls";
import { marchHint } from "./scenario/hints";

export interface ScenarioSetup {
  scenario: string;
  level: Level;
  tuning: Tuning;
  /**
   * Which team each army is on, indexed by `Side` — sides sharing a number are
   * allies, so `[0,1,2,3]` is a free-for-all and `[0,0,1,1]` is two pairs.
   *
   * **The assignment, not the name of a layout.** The named layouts are quick
   * fills on the screen and any army can be moved afterwards, so most of what a
   * player can set up here has no name.
   *
   * Ignored by every board that does not declare `teams` — which is every board
   * with two armies on it, where allying the only two sides there are would
   * make a game nobody can win.
   */
  teams: readonly number[];
  /** Undefined for a fresh roll; a number replays the same run exactly. */
  seed?: number;
  watch: boolean;
  /**
   * Watch the machine play a whole **run** rather than a board. `scenario` is
   * unread when this is set.
   *
   * A flag rather than a scenario id, because `"run"` is deliberately not one:
   * `scenarioById` falls back to the first board for anything it does not
   * know, so a run that arrived as an id would open with the chess position
   * laid over its level. That trap is documented on `hasScenario` and this is
   * the shape that cannot reach it.
   */
  run?: boolean;
  /**
   * A ceiling on how often the board draws, in frames a second. Undefined and
   * `0` are both off, which is the default.
   *
   * **Frames, never speed.** Everything still animates at the tempo it was
   * going to; what changes is how many pictures of it get drawn, which is what
   * makes this a knob about the machine rather than about the game. The board
   * plays identically at 15 and uncapped — it is simply seen fewer times on the
   * way, and the planner gets the cores back.
   */
  maxFps?: number;
}

const Head = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.kit.space.md,
  marginBottom: theme.kit.space.lg
}));

/**
 * Two columns, not one tall one. Everything used to be a 640px column:
 * settings, then a heading, then the boards below the fold. Neither half wants
 * to be narrow, so on any real window they sit side by side and the whole
 * screen is one screen. It collapses back at the width where the old shape was
 * the right one.
 */
const Columns = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(380px, 4fr) minmax(340px, 5fr)",
  gap: theme.kit.space.lg,
  alignItems: "start",
  "@media (max-width: 900px)": { gridTemplateColumns: "1fr" }
}));

const SettingsPanel = styled(Panel)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.md,
  padding: theme.kit.space.md
}));

/** A hint that has to be discoverable: dotted, and the cursor says so. */
const Help = styled("span")(({ theme }) => ({
  fontFamily: theme.kit.font.body,
  fontSize: 12,
  color: theme.kit.color.inkDim,
  borderBottom: `1px dotted ${theme.kit.color.lineHi}`,
  cursor: "help"
}));

const Advanced = styled("div")(({ theme }) => ({
  marginTop: 2,
  paddingTop: theme.kit.space.sm,
  borderTop: `2px solid ${theme.kit.color.line}`
}));

const AdvancedToggle = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  width: "100%",
  padding: "2px 0",
  border: 0,
  background: "transparent",
  color: theme.kit.color.inkDim,
  fontFamily: theme.kit.font.display,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: ".08em",
  textTransform: "uppercase",
  cursor: "pointer",
  '&:hover, &[aria-expanded="true"]': { color: theme.kit.color.ink },
  '&[aria-expanded="true"] .caret': { transform: "rotate(90deg)" }
}));

/** Rotates when the fold opens. `transform` only, so the row does not move. */
const Caret = styled("span")({ fontSize: 10, transition: "transform 140ms" });

const AdvancedBody = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.sm,
  paddingTop: theme.kit.space.md
}));

export function ScenarioScreen({
  onStart,
  onBack,
  catalog: given
}: {
  onStart(setup: ScenarioSetup): void;
  onBack(): void;
  /** The game's tables, where the screen is drawn outside the `GameContext`. */
  catalog?: Catalog;
}) {
  const catalog = useCatalog(given);
  const scenarios = catalog.scenarios();
  const alliances = catalog.alliances();
  const mostSides = catalog.mostSides();
  const [level, setLevel] = useState<Level>("easy");
  const [watch, setWatch] = useState(false);
  const [seed, setSeed] = useState("");
  /**
   * Undefined means "whatever the difficulty says", which is what lets the
   * sliders follow a difficulty change instead of pinning it.
   */
  const [tuning, setTuning] = useState<Tuning>({});
  /**
   * Whether the two tuning sliders are showing. **Closed to begin with**, and
   * that is a claim about them rather than tidiness: they default to the
   * difficulty and following it is the right answer until you have a reason
   * not to, so open is the state that needs asking for.
   */
  const [advanced, setAdvanced] = useState(false);
  /**
   * Which board is about to be played.
   *
   * **A selection now, where clicking a tile used to launch.** Every per-army
   * control below has to know how many armies there are and whether they can be
   * divided into teams, and only the board says — so the screen has to hold one
   * before it can ask anything honest about it. The run is a flag beside the id
   * rather than an id of its own, because `"run"` is deliberately not a
   * scenario: `scenarioById` falls back to the first board for anything it does
   * not know, which is the trap `hasScenario` exists for.
   */
  const [picked, setPicked] = useState<{ id: string; run: boolean }>({
    id: scenarios[0].id,
    run: false
  });
  /**
   * Which team each army is on. Free-for-all to begin with, which is what a
   * board opens as and what the layouts are a departure from.
   */
  const [teams, setTeams] = useState<number[]>(() =>
    Array.from({ length: mostSides }, (_, side) => alliances[0].sides[side] ?? side)
  );
  /**
   * A ceiling on how often the board draws. `0` is off, which is the default.
   *
   * **Here rather than in the View menu**, unlike the animation speed, because
   * it is not a taste — it is a trade against the machine, and the board where
   * the trade is real is this one: a scenario is where a planner is searched
   * against a renderer for the same cores, often for an hour with nobody
   * touching it. On a run or a versus game there is one turn of thinking
   * between your own moves and nothing to hand the frames to.
   */
  const [maxFps, setMaxFps] = useState<number>(0);

  const skill = SKILL[level];
  const aggression = tuning.aggression ?? skill.aggression;
  const greed = tuning.greed ?? skill.greed;
  /** Has either knob been moved off the difficulty's own number? */
  const tuned = tuning.aggression !== undefined || tuning.greed !== undefined;

  /** The chosen board, and how many armies stand on it. */
  const board = scenarioById(picked.id);
  const chosen = scenarios.find((scenario) => scenario.id === picked.id) ?? scenarios[0];
  /**
   * How many armies this game has a say about.
   *
   * **A run gets none.** It is one player's whole campaign rather than a
   * position, its camps pick their own styles as they land, and `App` throws
   * these settings away for that mode anyway — so offering them would be three
   * controls that quietly reach nothing.
   */
  const sides = picked.run ? 0 : chosen.sides;

  /** Which named layout the teams currently are, or undefined for a custom one. */
  const layout = layoutFor(teams, sides);
  /**
   * Which armies get a row: every one, where the board has teams. A board
   * without them has nothing per army to ask — the profile picker retired
   * with every profile but one (§31 R5).
   */
  const rows = board.teams ? Array.from({ length: sides }, (_, side) => side) : [];

  const start = () =>
    onStart({
      scenario: picked.run ? "" : picked.id,
      run: picked.run,
      // Trimmed to the board's own armies: the state is sized to the largest
      // board there is, and entries past this one's would be teams for armies
      // that are not on it.
      teams: teams.slice(0, sides),
      level,
      tuning,
      // Blank is a fresh roll. A number replays a run exactly, which is the
      // point of typing one in — the seed decides every drop, every wave and
      // every blunder, so two runs on one seed are the same run.
      seed: seed.trim() === "" ? undefined : Number(seed.trim()),
      watch,
      // Nought is off, and it is carried as `undefined` for the same reason the
      // two picks above are: an absent setting reads as "leave it alone" all
      // the way down, where a nought is a number the renderer would have to
      // know means nothing.
      maxFps: maxFps || undefined
    });

  return (
    <Page>
      <PageColumn width={1180}>
        <Head>
          <HoverCard
            placement="bottom-start"
            card="Back to the front door: a run, a versus game, or a scenario."
          >
            <Btn variant="ghost" small data-testid="scenario-back" onClick={onBack}>
              ←
            </Btn>
          </HoverCard>
          <PageTitle style={{ margin: 0, fontSize: 22 }}>Scenario</PageTitle>
          <HoverCard
            placement="bottom"
            maxWidth={320}
            card={
              "A board set up to show one thing: a working factory, a graveyard " +
              "to mine, a lane that runs the wrong way. Play it yourself, or sit " +
              "back and watch two machines play it."
            }
          >
            <Help>what is this?</Help>
          </HoverCard>
        </Head>

        <Columns>
          <SettingsPanel solid>
            <Setting
              label="Who plays"
              hint={
                "Play it and you take White. Watch and both armies are the " +
                "machine's — which is the measurement rig for the industrial half."
              }
            >
              {[
                { id: false, label: "Play it", hint: "You take White." },
                {
                  id: true,
                  label: "Watch",
                  hint: "Both armies are the machine's. Nothing is yours."
                }
              ].map((entry) => (
                <Choice
                  key={String(entry.id)}
                  testid={`mode-${entry.id ? "watch" : "play"}`}
                  label={entry.label}
                  hint={entry.hint}
                  on={watch === entry.id}
                  onClick={() => setWatch(entry.id)}
                />
              ))}
            </Setting>

            <Setting
              label="Difficulty"
              hint="How well it plays: how often it blunders, how far it looks, and how heavy a camp it reaches for."
            >
              {/* Difficulty is named things, so the row has that many stops. A
                  continuous one would have to interpolate a blunder rate, a
                  horizon and a reinforcement tier, and "37% hard" is not a
                  thing any of those mean. What is genuinely continuous — how
                  hard it comes for you and how much it plays the factory —
                  gets its own slider below. */}
              {catalog.difficulties().map((entry) => {
                const id = entry.id as Level;
                return (
                  <Choice
                    key={id}
                    testid={`level-${id}`}
                    label={entry.name}
                    hint={entry.detail}
                    on={level === id}
                    onClick={() => {
                      setLevel(id);
                      // The sliders follow the difficulty rather than pinning
                      // it: picking Hard and finding the aggression still on
                      // Easy's number is a setting that lies about itself.
                      setTuning({});
                    }}
                  />
                );
              })}
            </Setting>

            {/* **Shown only where it means something**, which it can be now
                that a board is chosen before it is played: allying the only two
                sides a two-army board has is a game in which neither can take
                anything, so there is nothing to ask there. It used to be always
                on and merely *say* so, because a tile click launched and the
                settings had no board to be about.

                The layouts are quick fills and the table below is the whole
                answer — which one is lit is derived by comparing the teams, so
                moving one army lands on Custom rather than leaving a preset lit
                beside teams it has stopped describing. */}
            {board.teams && (
              <Setting
                label="Teams"
                hint="Who is on whose side. Armies sharing a team cannot take each other and do not give check, so a team wins together — and the last team standing wins."
              >
                {alliances.map((entry) => (
                  <Choice
                    key={entry.id}
                    testid={`alliance-${entry.id}`}
                    label={entry.name}
                    hint={entry.detail}
                    on={layout?.id === entry.id}
                    onClick={() =>
                      setTeams((was) => was.map((team, side) => entry.sides[side] ?? team))
                    }
                  />
                ))}
                <Choice
                  testid="alliance-custom"
                  label="Custom"
                  hint="Whatever the table says. Lit when the teams are not one of the named layouts — it is a report rather than a mode, so clicking it puts every army back on a team of its own."
                  on={layout === undefined}
                  onClick={() => setTeams((was) => was.map((_, side) => side))}
                />
              </Setting>
            )}

            {/* **One row per army, because every question here is per army.**

                Stacked as a label and a full-width control each — which is what
                every other setting on this screen is — the four-way board came
                to twelve of them and you could not see two armies at once. This
                screen's whole job is the comparison (§15.8.1), and that is a
                question about a row against a row. */}
            {rows.length > 0 && (
              <Setting
                label="Armies"
                hint="Which team each army is on."
                plain
              >
                <ArmyTable>
                  <ArmyHead />
                  <HoverCard
                    placement="bottom"
                    card="Armies sharing a team are allied from the opening."
                  >
                    <ArmyHead>Team</ArmyHead>
                  </HoverCard>

                  {rows.map((side) => (
                    <Fragment key={`army-row-${side}`}>
                      <HoverCard placement="right" card={`${armyName(side)} ${marchHint(side)}`}>
                        <ArmySide>
                          {armyName(side)}
                          {!watch && side === 0 && <ArmyYou>you</ArmyYou>}
                        </ArmySide>
                      </HoverCard>

                      <Picker
                        testid={`team-${side}`}
                        label={`Team for ${armyName(side)}`}
                        value={String(teams[side] ?? side)}
                        onChange={(value) =>
                          setTeams((was) =>
                            was.map((team, at) => (at === side ? Number(value) : team))
                          )
                        }
                        // Every team is a real answer, so there is no blank
                        // entry: an army is always on one, and being alone on
                        // it is what a free-for-all is.
                        options={teamOptions(sides)}
                      />
                    </Fragment>
                  ))}
                </ArmyTable>
              </Setting>
            )}

            <Setting
              label="Seed"
              hint="Blank rolls a fresh one. A number replays the same game exactly — the same waves, the same drops, the same blunders."
              plain
            >
              <Field
                data-testid="scenario-seed"
                value={seed}
                onChange={(event) => setSeed(event.target.value.replace(/[^0-9]/g, ""))}
                placeholder="fresh"
                inputMode="numeric"
                aria-label="Seed"
              />
            </Setting>

            {/* **A ceiling on drawing, not on playing.** The board runs at the
                same speed at every setting — how fast it *plays* is the
                animation speed in Settings, which is a taste and belongs with
                the board. This is the other question: a scenario is a planner
                and a renderer wanting the same cores, and the planner is the
                half doing the thing being watched. Frames it does not take are
                frames the search gets. */}
            <Setting
              label="Frame cap"
              hint="How often the board is allowed to draw. It plays at the same speed either way — capping hands the cores back to the machine doing the thinking, which is worth it on a board nobody is touching."
            >
              {FRAME_CAPS.map((fps) => (
                <Choice
                  key={fps}
                  testid={`frame-cap-${fps}`}
                  label={fps === 0 ? "Uncapped" : `${fps} fps`}
                  hint={
                    fps === 0
                      ? "Draw on every frame the display offers."
                      : `Draw at most ${fps} times a second, whatever the display offers.`
                  }
                  on={maxFps === fps}
                  onClick={() => setMaxFps(fps)}
                />
              ))}
            </Setting>

            {/* **Folded away, and it starts folded.** These two are the reason
                the screen exists and the last thing most people want: they
                default to the difficulty, and letting them follow it is right
                until you have a reason. Open is the state that needs asking
                for — so the summary says whether anything has been moved,
                which is the one fact a closed panel must not hide. */}
            <Advanced>
              <AdvancedToggle
                data-testid="scenario-advanced"
                aria-expanded={advanced}
                onClick={() => setAdvanced((open) => !open)}
              >
                <Caret className="caret">▶</Caret>
                Advanced
                <span style={{ flex: 1 }} />
                <Text
                  role="display"
                  tone={tuned ? "cream" : "dim"}
                  size={12}
                  weight={400}
                  style={{ textTransform: "none", letterSpacing: 0 }}
                >
                  {tuned ? "edited" : `${aggression} / ${greed}`}
                </Text>
              </AdvancedToggle>

              <Collapse in={advanced} unmountOnExit>
                <AdvancedBody>
                  <Setting
                    label={`Aggression ${aggression}`}
                    hint="Centipawns per square closed toward you, on a quiet move. The knob that decides whether it comes for you at all."
                    plain
                  >
                    <Slider
                      data-testid="tune-aggression"
                      value={aggression}
                      min={0}
                      max={TUNING_MAX}
                      step={1}
                      onChange={(_, value) =>
                        setTuning((was) => ({ ...was, aggression: value as number }))
                      }
                    />
                  </Setting>

                  <Setting
                    label={`Greed ${greed}`}
                    hint="The same, toward factory work: standing on scrap, hauling a load, and building a piece instead of moving one. Above aggression it stops playing chess."
                    plain
                  >
                    <Slider
                      data-testid="tune-greed"
                      value={greed}
                      min={0}
                      max={TUNING_MAX}
                      step={1}
                      onChange={(_, value) =>
                        setTuning((was) => ({ ...was, greed: value as number }))
                      }
                    />
                  </Setting>

                  {tuned && (
                    <Btn
                      small
                      variant="ghost"
                      style={{ alignSelf: "flex-start" }}
                      data-testid="tune-reset"
                      onClick={() => setTuning({})}
                    >
                      Back to {catalog.difficulties().find((entry) => entry.id === level)!.name}
                    </Btn>
                  )}
                </AdvancedBody>
              </Collapse>
            </Advanced>
          </SettingsPanel>

          <div>
            <Setting
              label="Pick a board"
              hint="Each of these is a position set up to show one thing. Hover a square to read what it is for."
              plain
            >
              {/* Squares, and only a name on each. What each board is *for* is a
                  sentence, and eleven sentences is a wall of prose in front of a
                  choice between eleven things — so the sentence is on hover and
                  what is printed is the name. */}
              <Boards>
                {scenarios.map((scenario) => (
                  <BoardTile
                    key={scenario.id}
                    testid={`scenario-${scenario.id}`}
                    name={scenario.name}
                    hint={scenario.detail}
                    on={!picked.run && picked.id === scenario.id}
                    onClick={() => setPicked({ id: scenario.id, run: false })}
                  />
                ))}

                {/* **The run, which is not a board.**

                    Everything above is a position; this is the whole
                    single-player loop played by the machine — the level, its
                    objectives, the camps that arrive when a front is cleared.
                    It sits in the grid because it is the same kind of choice
                    (what am I about to watch), and it is marked apart because
                    picking it ignores half the settings: there is no army pick
                    and no alliance on a run, only how well it plays and which
                    seed it rolls. */}
                <BoardTile
                  testid="scenario-watch-run"
                  name="Watch a run"
                  hint={
                    "The machine plays the single-player run from the first level: " +
                    "objectives, cleared fronts, camps arriving and the board growing. " +
                    "Nothing here is yours and nothing is saved."
                  }
                  run
                  on={picked.run}
                  onClick={() => setPicked((was) => ({ ...was, run: true }))}
                />
              </Boards>
            </Setting>

            {/* **The one thing on this screen that starts a game.** A tile used
                to, and then every setting above had to be written as though it
                had no board to be about — the teams control said out loud that
                it only meant something on some other board, and there was no way
                at all to name an army for a side the two-army boards do not
                have. Selecting first costs one click and buys the panel the one
                fact it needs. */}
            <Btn
              variant="primary"
              data-testid="scenario-start"
              style={{ width: "100%", marginTop: 12, padding: 12, fontSize: 15 }}
              onClick={start}
            >
              {picked.run ? "Watch a run" : `${watch ? "Watch" : "Play"} ${board.name}`}
            </Btn>
          </div>
        </Columns>
      </PageColumn>
    </Page>
  );
}

/**
 * The teams there are to be on: one per army, so nobody has to share.
 *
 * Letters rather than numbers, because the value is an *identity* and a numeral
 * beside a side number is two different meanings of "2" in one row.
 */
function teamOptions(sides: number): readonly { id: string; name: string }[] {
  return Array.from({ length: sides }, (_, team) => ({
    id: String(team),
    name: `Team ${String.fromCharCode(65 + team)}`
  }));
}
